import { useEffect, useState } from "react";
import { BookOpen, Mic, Flame } from "lucide-react";
import { getWeekStats, getPerfectStreak, WRITTEN_TARGET_MIN, ORAL_TARGET_MIN } from "../lib/dailyPlan";
import Card from "./atoms/Card";
import Chip from "./atoms/Chip";

function useWeeklyData() {
  const [data, setData] = useState(() => ({ ...getWeekStats(), streak: getPerfectStreak() }));
  useEffect(() => {
    const refresh = () => setData({ ...getWeekStats(), streak: getPerfectStreak() });
    const id = setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(id); window.removeEventListener("focus", refresh); };
  }, []);
  return data;
}

export default function WeeklyProgressChart({ className = "" }) {
  const { days, perfect, partial, missed, streak } = useWeeklyData();
  const today = days.length - 1;

  return (
    <Card className={className} style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18, gap: 12, flexWrap: "wrap" }}>
        <div>
          <span className="eyebrow">This week</span>
          <h3 className="font-display" style={{ fontSize: 26, fontWeight: 400, lineHeight: 1.15, margin: "4px 0 2px" }}>
            Seven days, <em style={{ color: "var(--gold-500)" }}>side by side.</em>
          </h3>
          <p style={{ color: "var(--fg-3)", fontSize: 13, margin: 0 }}>Written above, oral below. Both filled = a clean day.</p>
        </div>
        {streak > 0 && (
          <Chip tone="rust" icon={<Flame size={12} strokeWidth={1.8} />}>
            {streak}-day streak
          </Chip>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginBottom: 16 }}>
        {days.map((d, i) => {
          const writtenPct = Math.min(100, Math.round((d.writtenMinutes / WRITTEN_TARGET_MIN) * 100));
          const oralPct    = Math.min(100, Math.round((d.oralMinutes    / ORAL_TARGET_MIN)    * 100));
          const isToday    = i === today;
          return (
            <div key={d.date} style={{
              padding: 8,
              borderRadius: "var(--radius-sm)",
              background: isToday ? "var(--paper-deep)" : "transparent",
              border: isToday ? "1px solid var(--gold-500)" : "1px solid transparent",
              transition: "all 240ms cubic-bezier(.2,.8,.2,1)",
            }}>
              <p style={{ fontSize: 10, fontWeight: 700, textAlign: "center", color: isToday ? "var(--ink-900)" : "var(--fg-3)", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                {d.label.slice(0, 1)}
              </p>
              <Bar pct={writtenPct} color="var(--gold-500)" />
              <div style={{ height: 4 }} />
              <Bar pct={oralPct} color="var(--rust)" />
              <p style={{ textAlign: "center", margin: "8px 0 0", fontSize: 10, color: d.complete ? "var(--sage-dark)" : "var(--fg-3)" }}>
                {d.complete ? "·●·" : ""}
              </p>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--fg-3)", flexWrap: "wrap", marginBottom: 18 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <BookOpen size={12} strokeWidth={1.5} color="var(--gold-500)" /> Written
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Mic size={12} strokeWidth={1.5} color="var(--rust)" /> Oral
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        <SummaryStat value={perfect} label="Perfect" tone="sage" />
        <SummaryStat value={partial} label="Partial" tone="gold" />
        <SummaryStat value={missed}  label="Missed"  tone={missed > 0 ? "berry" : "neutral"} />
      </div>
    </Card>
  );
}

function Bar({ pct, color }) {
  return (
    <div style={{ height: 36, background: "rgba(11,17,29,0.06)", borderRadius: 3, overflow: "hidden", display: "flex", alignItems: "flex-end" }}>
      <div style={{ width: "100%", height: `${pct}%`, background: color, transition: "height 700ms cubic-bezier(.2,.8,.2,1)" }} />
    </div>
  );
}

function SummaryStat({ value, label, tone }) {
  const colors = {
    sage: "var(--sage-dark)",
    gold: "var(--gold-700)",
    berry: "var(--berry)",
    neutral: "var(--fg-3)",
  };
  return (
    <div style={{ background: "var(--paper-deep)", borderRadius: "var(--radius-sm)", padding: 10, textAlign: "center" }}>
      <p className="font-display" style={{ fontSize: 26, lineHeight: 1, color: colors[tone], margin: 0 }}>{value}</p>
      <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--fg-3)", margin: "4px 0 0" }}>{label}</p>
    </div>
  );
}
